import type { OmsResourceStatusCounts } from '../types';

/* ============================================================
 * Health Score — the performance model behind the Explorer.
 *
 * Target model (per spec):
 *   40% completion · 25% on-time · 20% overdue pressure ·
 *   10% activity recency · 5% quality.
 *
 * The API does not expose per-task completed-vs-due dates yet, so the
 * on-time component cannot be computed honestly. Its weight is
 * redistributed (TODO: restore a 25% on-time component once the
 * backend exposes per-task due/decided dates):
 *   45% completion · 25% overdue pressure ·
 *   15% activity recency · 15% quality.
 * Every component carries its own honest definition + denominator so
 * the UI can show exactly how a score was earned.
 * ============================================================ */

export type HealthBand = 'excellent' | 'good' | 'watch' | 'attention';

export const BAND_META: Record<HealthBand, { label: string }> = {
  excellent: { label: 'Excellent' },
  good: { label: 'Good' },
  watch: { label: 'Watch' },
  attention: { label: 'Needs attention' },
};

export interface HealthComponent {
  key: 'completion' | 'overduePressure' | 'recency' | 'quality';
  label: string;
  /** Effective weight actually applied (after renormalisation), 0–1. */
  weight: number;
  /** 0–100. */
  score: number;
  /** Honest definition with denominator, e.g. "18 of 40 scheduled". */
  detail: string;
}

export interface HealthScore {
  /** Weighted 0–100 composite. */
  score: number;
  band: HealthBand;
  components: HealthComponent[];
}

export interface HealthInput {
  counts: OmsResourceStatusCounts;
  /** Calendar days since the entity's last pass/fail submission (null = never). */
  daysSinceLastSubmit: number | null;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** Days of silence after which the recency component reaches zero. */
export const RECENCY_ZERO_DAYS = 8;

export function bandOf(score: number): HealthBand {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'watch';
  return 'attention';
}

export const completedOf = (c: OmsResourceStatusCounts) => c.pass + c.fail;
export const openOf = (c: OmsResourceStatusCounts) => c.inprogress + c.due + c.overdue + c.hold;
export const scheduledOf = (c: OmsResourceStatusCounts) => completedOf(c) + openOf(c);

/**
 * Compute the Health Score for one entity in one window.
 * Returns null when the entity has no scheduled work in the window —
 * "no data" must never masquerade as a real grade.
 */
export function computeHealthScore(input: HealthInput): HealthScore | null {
  const { counts, daysSinceLastSubmit } = input;
  const completed = completedOf(counts);
  const open = openOf(counts);
  const scheduled = scheduledOf(counts);
  if (scheduled === 0) return null;

  const components: Array<HealthComponent & { rawWeight: number }> = [];

  const completionRate = completed / scheduled;
  components.push({
    key: 'completion',
    label: 'Completion',
    rawWeight: 0.45,
    weight: 0,
    score: Math.round(completionRate * 100),
    detail: `${completed} completed of ${scheduled} scheduled`,
  });

  const overdueSafe = open > 0 ? clamp01(1 - counts.overdue / open) : 1;
  components.push({
    key: 'overduePressure',
    label: 'Overdue pressure',
    rawWeight: 0.25,
    weight: 0,
    score: Math.round(overdueSafe * 100),
    detail: open > 0 ? `${counts.overdue} overdue of ${open} open` : 'No open tasks',
  });

  const recency =
    daysSinceLastSubmit === null ? 0 : clamp01(1 - daysSinceLastSubmit / RECENCY_ZERO_DAYS);
  components.push({
    key: 'recency',
    label: 'Activity recency',
    rawWeight: 0.15,
    weight: 0,
    score: Math.round(recency * 100),
    detail:
      daysSinceLastSubmit === null
        ? 'No submission yet'
        : daysSinceLastSubmit === 0
          ? 'Submitted today'
          : `Last submission ${daysSinceLastSubmit}d ago`,
  });

  // Quality only grades work that was actually decided — with zero
  // completed checks it is excluded and the remaining weights renormalise,
  // so an idle entity is not silently gifted (or docked) a pass rate.
  if (completed > 0) {
    const passRate = counts.pass / completed;
    components.push({
      key: 'quality',
      label: 'Quality',
      rawWeight: 0.15,
      weight: 0,
      score: Math.round(passRate * 100),
      detail: `${counts.pass} pass of ${completed} completed checks`,
    });
  }

  const weightSum = components.reduce((s, c) => s + c.rawWeight, 0);
  let weighted = 0;
  for (const c of components) {
    c.weight = c.rawWeight / weightSum;
    weighted += c.score * c.weight;
  }
  const score = Math.round(weighted);

  return {
    score,
    band: bandOf(score),
    components: components.map(({ rawWeight: _raw, ...rest }) => rest),
  };
}

/** Signed delta between two scores; null when either window has no score. */
export function scoreDelta(current: HealthScore | null, previous: HealthScore | null): number | null {
  if (!current || !previous) return null;
  return current.score - previous.score;
}
